import { type FC, memo, useEffect, useMemo, useState, type JSX } from 'react';
import clsx from 'clsx';
import { Container } from 'Container';
import { useDeprioritizeComponents } from 'useDeprioritizeComponents';
import { useCurrentUser } from 'FrontRoyalAngular';
import { getCohort, getProgramInclusion } from 'Users';
import { EventList } from 'EventList';
import { useAppHeaderViewModel } from 'NavigationHelpers';
import { ProgramTypeConfigs } from 'Program';
import { StudentSpotlightCarousel } from 'StudentSpotlight';
import { Footer as FooterComponent } from 'Footer';
import { MeetYourClass } from 'MeetYourClass';
import { BookmarkedAndCompletedCourses } from 'BookmarkedAndCompletedCourses';
import { useSiteMetadata } from 'SearchEngineOptimization/useSiteMetadata';
import { StudentDashboardSidebar } from 'StudentDashboardSidebar';
import useStudentDashboardConfig from 'AdmissionsGuidance/useStudentDashboardConfig';
import { useFocusProject } from 'CurrentlyStudying/hooks/useFocusProject';
import { ProjectGroupInvitationModal } from 'ProjectGroup';
import { DirectLinkProjectFeedbackModal } from 'ProjectFeedback/DirectLinkProjectFeedbackModal';
import {
    DesktopCourseware as DesktopCoursewareComponent,
    MobileCourseware as MobileCoursewareComponent,
} from './Courseware';
import { useMobileSidebarResponsivePadding } from './useMobileSidebarResponsivePadding';
import { usePostApplyInterviewModal } from './usePostApplyInterviewModal';

function FeaturedStudentsAndEvents() {
    const currentUser = useCurrentUser();
    const studentNetworkActivated = useMemo(
        () => !!getProgramInclusion(currentUser)?.studentNetworkActivated,
        [currentUser],
    );
    const cohort = useMemo(() => getCohort(currentUser), [currentUser]);
    const programTypeConfig = cohort && ProgramTypeConfigs[cohort.programType];

    if (!currentUser || !cohort?.providesStudentNetworkAccess) return null;

    return (
        <div className="flex flex-col gap-7.5">
            {studentNetworkActivated ? (
                <MeetYourClass />
            ) : (
                <StudentSpotlightCarousel
                    {...(programTypeConfig?.studentSpotlightCarouselProps ?? { streamIds: [] })}
                />
            )}
            <EventList featured={!studentNetworkActivated} />
        </div>
    );
}

type StudentDashboardPresentationProps = {
    MobileCourseware: JSX.Element;
    DesktopCourseware: JSX.Element;
    FeaturedStudentsAndEvents: JSX.Element;
    Sidebar: JSX.Element | null;
    BookmarkedCompletedCourses: JSX.Element;
    Footer: JSX.Element;
    bottomPaddingClass: string;
};

export const StudentDashboardPresentation: FC<StudentDashboardPresentationProps> = ({
    MobileCourseware,
    DesktopCourseware,
    FeaturedStudentsAndEvents: FeaturedStudentsAndEventsComp,
    Sidebar,
    BookmarkedCompletedCourses: BookmarkedCompletedCoursesComp,
    Footer,
    bottomPaddingClass,
}) => (
    <div className={clsx('student-dashboard fade-in bg-slate-grey-100-alt', bottomPaddingClass)}>
        {MobileCourseware}
        <Container className="pb-9 sm:pb-14">
            <div className="mx-auto flex flex-col gap-3.5 sm:pt-10">
                {/* First Row */}
                <div className="flex flex-col justify-between gap-3.5 md:flex-row md:gap-7.5">
                    {/* Main Content */}
                    <div className="main-column flex w-full shrink-0 flex-col gap-3.5 md:w-2/3 md:grow-0 md:gap-7.5">
                        {DesktopCourseware}
                        {FeaturedStudentsAndEventsComp}
                    </div>
                    {Sidebar}
                </div>

                {/* Second Row */}
                <div className="bookmarked-courses-container w-full">{BookmarkedCompletedCoursesComp}</div>
            </div>
        </Container>
        {Footer}
    </div>
);

const StudentDashboardComponent: FC = () => {
    const AppHeaderViewModel = useAppHeaderViewModel();
    const SiteMetadata = useSiteMetadata();
    const studentDashboardConfig = useStudentDashboardConfig();
    const bottomPaddingClass = useMobileSidebarResponsivePadding();
    const currentUser = useCurrentUser();
    const invitedGroupProjectMembership = currentUser?.invitedProjectGroupMemberships?.[0];
    const showDeprioritizedComponents = useDeprioritizeComponents();

    const [isModalOpen, setIsModalOpen] = useState(true);
    useFocusProject();

    // Show post-apply interview modal if user is eligible (after application submission).
    usePostApplyInterviewModal();

    useEffect(() => {
        if (invitedGroupProjectMembership) setIsModalOpen(true);
    }, [invitedGroupProjectMembership]);

    useEffect(() => {
        if (AppHeaderViewModel.showAlternateHomeButton) {
            AppHeaderViewModel.showAlternateHomeButton = false;
        }
        if (AppHeaderViewModel.showCaret) {
            AppHeaderViewModel.showCaret = false;
        }
    }, [AppHeaderViewModel]);

    useEffect(() => {
        SiteMetadata.updateHeaderMetadata();
    }, [SiteMetadata]);

    return (
        <>
            {invitedGroupProjectMembership && (
                <ProjectGroupInvitationModal
                    closeModal={() => setIsModalOpen(false)}
                    isOpen={isModalOpen}
                    projectGroupMembership={invitedGroupProjectMembership}
                />
            )}
            {showDeprioritizedComponents && <DirectLinkProjectFeedbackModal />}
            <StudentDashboardPresentation
                MobileCourseware={<MobileCoursewareComponent studentDashboardConfig={studentDashboardConfig} />}
                DesktopCourseware={<DesktopCoursewareComponent studentDashboardConfig={studentDashboardConfig} />}
                FeaturedStudentsAndEvents={<FeaturedStudentsAndEvents />}
                Sidebar={
                    studentDashboardConfig ? (
                        <StudentDashboardSidebar studentDashboardConfig={studentDashboardConfig} />
                    ) : null
                }
                BookmarkedCompletedCourses={<BookmarkedAndCompletedCourses />}
                Footer={<FooterComponent />}
                bottomPaddingClass={bottomPaddingClass}
            />
        </>
    );
};

export const StudentDashboard = memo(StudentDashboardComponent) as typeof StudentDashboardComponent;
